// @flow

import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import * as R from "ramda";
import { Center, Spinner } from "@chakra-ui/react";
import { useSarah, Text } from "@unifize/sarah";

import { getFile as resolveFile } from "src/actions/file";
import {
  getFile,
  getCurrentRoomId,
  getConversationModalVisibility,
} from "src/reducers";
import Icon from "src/icons";
import useFileViewer from "src/hooks/useFileViewer";
import { FileData } from "src/components/Dock/Checklist/styles";
import FileThumbnail from "src/components/Dock/Checklist/File/FileThumbnail";

import type { UnifizeFile } from "src/types";

import {
  FileWrapper,
  FileIconWrapper,
  FileSize,
  DownloadButton,
  FileDataHeader,
} from "./styles";
import { FieldValue } from "../Values/styles";

type Props = {
  id: string,
  handleDownload: Function,
  old?: boolean,
};

const File = ({ id, handleDownload, old = false }: Props) => {
  const { colors } = useSarah();
  const file: ?UnifizeFile = useSelector(({ app }) => getFile(app, id));
  const dispatch = useDispatch();

  const roomId = useSelector(({ app }) => getCurrentRoomId(app));

  const modal = useSelector(({ app }) => getConversationModalVisibility(app));

  const { data: viewState, openFile } = useFileViewer({ file, modal, roomId });

  useEffect(() => {
    if (R.isEmpty(file || {})) {
      dispatch(resolveFile(id));
    }
  }, [id, file]);

  if (old && file) {
    return <FieldValue old>{file?.originalName}</FieldValue>;
  }

  return file ? (
    <FileWrapper
      onClick={() => openFile()}
      hoverBg={colors.background.secondary.subtle}
    >
      {viewState.fetching ? (
        <Center
          sx={{
            borderRadius: "50%",
            backgroundColor: "gray.50",
            m: "2px",
            width: "24px",
            height: "26px",
          }}
        >
          <Spinner emptyColor="gray.200" color="blue.500" />
        </Center>
      ) : (
        <FileIconWrapper>
          <FileThumbnail
            roomId={roomId}
            file={file}
            originalName={file.originalName}
          />
        </FileIconWrapper>
      )}
      <FileData>
        <FileDataHeader>
          <Text variant="caption" weight="bold" truncate>
            {file.originalName}
          </Text>
          <DownloadButton
            onClick={(e) => {
              e.stopPropagation();
              handleDownload(file);
            }}
            className="download-button"
          >
            <Icon type="download2" />
          </DownloadButton>
        </FileDataHeader>
        <FileSize>
          {/* Extract file extension: match one or more non-dot characters at the end of the string */}
          {(file.originalName.match(/[^.]+$/)?.[0] ?? "").toUpperCase()}
        </FileSize>
      </FileData>
    </FileWrapper>
  ) : null;
};

export default File;
